import React, { memo } from 'react';
import CampaignHeader from '../../shared/CampaignHeader';
import CampaignProgressMeta from '../../shared/CampaignHeader/components/CampaignProgressMeta';

/**
 * ColumnProgressBar - Monday.com style segmented progress bar showing task distribution
 */
const ColumnProgressBar = memo(({ columns, tasksByStatus, totalCount }) => {
  if (totalCount === 0) {
    return (
      <div className="column-progress-bar">
        <div className="column-progress-bar__track column-progress-bar__track--empty" />
      </div>
    );
  }

  // Calculate segments with percentages
  const segments = columns.map(column => {
    const count = tasksByStatus[column.id]?.length || 0;
    const percentage = (count / totalCount) * 100;
    return {
      id: column.id,
      name: column.name,
      color: column.color || '#6b7280',
      count,
      percentage,
    };
  }).filter(seg => seg.count > 0);

  return (
    <div className="column-progress-bar">
      <div className="column-progress-bar__track">
        {segments.map((segment) => (
          <div
            key={segment.id}
            className="column-progress-bar__segment"
            style={{
              width: `${segment.percentage}%`,
              backgroundColor: segment.color,
            }}
            title={`${segment.name}: ${segment.count} task${segment.count !== 1 ? 's' : ''} (${Math.round(segment.percentage)}%)`}
          />
        ))}
      </div>
    </div>
  );
});

/**
 * KanbanHeader - Campaign header with breadcrumb, icon, title, and progress
 * 
 * Uses the shared CampaignHeader component for breadcrumb, icon, and title.
 * Adds Kanban-specific segmented progress bar.
 */
const KanbanHeader = memo(({
  campaignName,
  campaignIcon,
  campaignProgress,
  breadcrumbPath,
  completedCount,
  totalCount,
  remainingMinutes = 0,
  remainingMinMinutes,
  missingEstCount = 0,
  remainingBreakdown = [],
  columns,
  tasksByStatus,
  onNavigateToParent,
  onIconChange,
  onSelectSprint,
  onRename,
}) => {
  if (!campaignName) return null;

  return (
    <CampaignHeader
      name={campaignName}
      icon={campaignIcon}
      breadcrumbPath={breadcrumbPath}
      onRename={onRename}
      onIconChange={onIconChange}
      onSelectSprint={onSelectSprint}
      classPrefix="kanban-header"
    >
      <CampaignProgressMeta
        completedCount={completedCount}
        totalCount={totalCount}
        remainingMinutes={remainingMinutes}
        remainingMinMinutes={remainingMinMinutes}
        missingEstCount={missingEstCount}
        remainingBreakdown={remainingBreakdown}
        progressPercent={campaignProgress}
        includeProgressPercent
        className="kanban-header__stats"
        countClassName="kanban-header__stats-text"
      />

      {/* Segmented column progress bar */}
      {columns && columns.length > 0 && (
        <ColumnProgressBar 
          columns={columns} 
          tasksByStatus={tasksByStatus} 
          totalCount={totalCount} 
        />
      )}
    </CampaignHeader>
  );
});

export default KanbanHeader;
